import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { Calculator, Loader2, Info } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Counter } from "@/components/site/Counter";
import api from "@/lib/api";

const fmt = (n) => (n || 0).toLocaleString("en-US");
const selectClass = "h-12 rounded-[var(--radius-sm)] border-[color:var(--bp-border)] bg-[color:var(--bp-bg-2)] text-[color:var(--bp-cream)]";
const labelClass = "text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bp-text-muted)]";

export const CompensationCalculator = () => {
  const [options, setOptions] = useState({ roles: [], levels: [], markets: [] });
  const [sel, setSel] = useState({ role: "", level: "", market: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.getCompOptions().then((o) => {
      setOptions(o);
      setSel({ role: o.roles[1]?.id || o.roles[0]?.id || "", level: o.levels[1]?.id || "", market: o.markets[0]?.id || "" });
    }).catch(() => setError("Could not load options."));
  }, []);

  const calculate = async () => {
    if (!sel.role || !sel.level || !sel.market) return;
    setLoading(true); setError("");
    try {
      const r = await api.calcComp(sel);
      setResult(r);
    } catch (e) { setError("Could not calculate. Please try again."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (sel.role && sel.level && sel.market) calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel.role, sel.level, sel.market]);

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.95fr_1.05fr]" data-testid="compensation-calculator">
      {/* Controls */}
      <div className="rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-7">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[color:var(--bp-teal)]/40 bg-[color:var(--bp-teal-soft)] text-[color:var(--bp-teal)]"><Calculator className="h-5 w-5" /></span>
          <h3 className="font-display text-xl font-semibold text-[color:var(--bp-cream)]">Compensation Calculator</h3>
        </div>
        <p className="mt-3 text-[13.5px] text-[color:var(--bp-text-muted)]">Model indicative base, bonus and total compensation by role, seniority and market.</p>

        <div className="mt-6 space-y-5">
          <div className="space-y-2">
            <Label className={labelClass}>Role</Label>
            <Select value={sel.role} onValueChange={(v) => setSel((s) => ({ ...s, role: v }))}>
              <SelectTrigger data-testid="calc-role-select" className={selectClass}><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent>{options.roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>Experience</Label>
            <Select value={sel.level} onValueChange={(v) => setSel((s) => ({ ...s, level: v }))}>
              <SelectTrigger data-testid="calc-level-select" className={selectClass}><SelectValue placeholder="Select experience" /></SelectTrigger>
              <SelectContent>{options.levels.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className={labelClass}>Market</Label>
            <Select value={sel.market} onValueChange={(v) => setSel((s) => ({ ...s, market: v }))}>
              <SelectTrigger data-testid="calc-market-select" className={selectClass}><SelectValue placeholder="Select market" /></SelectTrigger>
              <SelectContent>{options.markets.map((m) => <SelectItem key={m.id} value={m.id}>{m.label} ({m.currency})</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {error && <p className="mt-4 text-[13px] text-[color:var(--bp-danger)]">{error}</p>}
      </div>

      {/* Result */}
      <div className="rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-bg-2)] p-7" data-testid="calc-result">
        {loading && !result ? (
          <div className="flex h-full items-center justify-center text-[color:var(--bp-text-muted)]"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : result ? (
          <div>
            <p className="eyebrow text-[color:var(--bp-teal)]">{result.role} · {result.level} · {result.market}</p>
            <div className="mt-4">
              <p className="text-[12px] uppercase tracking-[0.12em] text-[color:var(--bp-text-muted)]">Total compensation range</p>
              <p className="mt-1 font-display text-3xl font-semibold text-[color:var(--bp-gold)] sm:text-4xl">
                {result.currency} {fmt(result.total.low)} – {fmt(result.total.high)}
              </p>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="rounded-[var(--radius-md)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-4">
                <p className="text-[11px] uppercase tracking-[0.1em] text-[color:var(--bp-text-muted)]">Base salary</p>
                <p className="mt-1 font-display text-lg font-semibold text-[color:var(--bp-cream)]">{result.currency} {fmt(result.base.low)} – {fmt(result.base.high)}</p>
              </div>
              <div className="rounded-[var(--radius-md)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-4">
                <p className="text-[11px] uppercase tracking-[0.1em] text-[color:var(--bp-text-muted)]">Bonus</p>
                <p className="mt-1 font-display text-lg font-semibold text-[color:var(--bp-cream)]">{result.bonus_pct.low}–{result.bonus_pct.high}% of base</p>
              </div>
            </div>
            <div className="mt-5 h-[150px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={result.chart} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid horizontal={false} stroke="#144970" strokeOpacity={0.4} />
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#C5BFB6" }} width={110} />
                  <Tooltip cursor={{ fill: "rgba(45,181,165,0.08)" }} formatter={(v) => `${result.currency} ${fmt(v)}`} contentStyle={{ background: "#0D2B4D", border: "1px solid #144970", borderRadius: 10, color: "#F5F1EB" }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {result.chart.map((_, i) => <Cell key={i} fill={i === 0 ? "#2DB5A5" : "#D4AF37"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-4 flex items-start gap-2 text-[12px] leading-relaxed text-[color:var(--bp-text-muted)]/85"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {result.note}</p>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-[color:var(--bp-text-muted)]">Select role, experience and market.</div>
        )}
      </div>
    </div>
  );
};
